import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Passwordless landing page. Exchanges the JWT in `?token=...` for an access
 * token, stores it like a regular login, then drops the user straight into
 * the dashboard.
 *
 * NOTE: We use a ref-guard because React 18 dev Strict-Mode mounts effects
 * twice — the magic-link is single-use, so a double-call would burn the token
 * before the user ever logs in.
 */
export default function MagicLoginPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { applyAuthResponse } = useAuth();
    const [error, setError] = useState("");
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        const token = params.get("token");
        if (!token) {
            setError("Kein Login-Token in der URL gefunden.");
            return;
        }
        api.post("/auth/magic-login", { token })
            .then(({ data }) => {
                if (typeof applyAuthResponse === "function") applyAuthResponse(data);
                navigate("/dashboard", { replace: true });
            })
            .catch((err) => {
                setError(
                    formatApiError(err.response?.data?.detail) ||
                    "Login fehlgeschlagen. Bitte erneut anfragen.",
                );
            });
    }, [params, navigate, applyAuthResponse]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50/40 to-amber-50/30 flex items-center justify-center p-5">
            <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-8 text-center border border-slate-100">
                {!error ? (
                    <>
                        <div className="mx-auto h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
                            <Loader2 className="text-red-600 animate-spin" size={26} />
                        </div>
                        <h1 className="font-heading text-xl font-bold text-slate-900">Du wirst eingeloggt...</h1>
                        <p className="mt-2 text-sm text-slate-500">Einen Moment bitte.</p>
                    </>
                ) : (
                    <div data-testid="magic-login-error">
                        <div className="mx-auto h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
                            <AlertTriangle className="text-red-600" size={26} />
                        </div>
                        <h1 className="font-heading text-xl font-bold text-slate-900">Login fehlgeschlagen</h1>
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{error}</p>
                        <button
                            onClick={() => navigate("/login")}
                            className="mt-6 px-6 py-2.5 rounded-full font-semibold bg-slate-900 text-white text-sm"
                        >
                            Zum normalen Login
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
